import React, { useEffect, useRef, useState } from "react";

import {
  FaPlus,
  FaTrash,
  FaTimes,
  FaSave,
  FaImage,
  FaImages,
  FaUpload,
} from "react-icons/fa";

import AOS from "aos";
import "aos/dist/aos.css";

import "./Gallery.css";
import { BASE_URL } from "../../Config/config";

export default function Gallery() {
  const fileInputRef = useRef(null);

  const [gallery, setGallery] = useState([]);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);

  const [preview, setPreview] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  /*
  |--------------------------------------------------------------------------
  | FETCH GALLERY
  |--------------------------------------------------------------------------
  */

  const fetchGallery = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/gallery.php?action=get`);

      const result = await response.json();

      console.log("GALLERY API:", result);

      if (result.status) {
        const galleryData = result.data || [];

        setGallery(galleryData);
        setCurrentPage(1);
      } else {
        alert(result.message || "Failed to load gallery");
      }
    } catch (error) {
      console.error("Fetch gallery error:", error);

      alert("Unable to load gallery");
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    AOS.init({
      duration: 700,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);

  useEffect(() => {
    fetchGallery();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | OPEN ADD FORM
  |--------------------------------------------------------------------------
  */

  const openAddForm = () => {
    setSelectedFile(null);

    setPreview("");

    setShowForm(true);
  };

  /*
  |--------------------------------------------------------------------------
  | IMAGE CHANGE
  |--------------------------------------------------------------------------
  */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image");

      return;
    }

    setSelectedFile(file);

    setPreview(URL.createObjectURL(file));
  };

  /*
  |--------------------------------------------------------------------------
  | SAVE IMAGE
  |--------------------------------------------------------------------------
  */

  const saveGallery = async () => {
    if (!selectedFile) {
      alert("Please select an image");

      return;
    }

    const formData = new FormData();

    formData.append("image", selectedFile);

    try {
      setSaving(true);

      const response = await fetch(`${BASE_URL}/gallery.php?action=add`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      console.log("ADD GALLERY:", result);

      if (!result.status) {
        alert(result.message || "Failed to upload image");

        return;
      }

      alert("Gallery image uploaded successfully");

      await fetchGallery();

      closeForm();
    } catch (error) {
      console.error("Upload gallery error:", error);

      alert("Something went wrong while uploading");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const deleteGallery = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this image?",
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${BASE_URL}/gallery.php?action=delete&id=${id}`,
        {
          method: "DELETE",
        },
      );

      const result = await response.json();

      console.log("DELETE GALLERY:", result);

      if (!result.status) {
        alert(result.message || "Failed to delete image");

        return;
      }

      alert("Image deleted successfully");

      await fetchGallery();
    } catch (error) {
      console.error("Delete gallery error:", error);

      alert("Unable to delete image");
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE FORM
  |--------------------------------------------------------------------------
  */

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);

    setSelectedFile(null);

    setPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="admin-gallery-page">
        <div className="admin-gallery-empty">
          <FaImages />

          <h3>Loading Gallery...</h3>

          <p>Please wait.</p>
        </div>
      </div>
    );
  }
  const totalPages = Math.ceil(gallery.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const currentGallery = gallery.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="admin-gallery-page">
      {/* HEADER */}

      <div
        className="admin-page-header"
        data-aos="fade-down"
        data-aos-duration="700"
      >
        <div>
          <span className="admin-page-label">GALLERY MANAGEMENT</span>

          <h1>Gallery</h1>

          <p>Manage photos displayed on the Star Computers website.</p>
        </div>

        <button className="admin-primary-btn" onClick={openAddForm}>
          <FaPlus />
          Add Photo
        </button>
      </div>

      {/* STATISTICS */}
      <div
        className="gallery-admin-stats"
        data-aos="fade-up"
        data-aos-delay="150"
      >
        <div className="gallery-stat-card">
          <div className="gallery-stat-icon">
            <FaImages />
          </div>

          <div>
            <span>Total Photos</span>

            <strong>{gallery.length}</strong>
          </div>
        </div>
      </div>

      {/* GALLERY */}

      <div
        className="admin-gallery-section"
        data-aos="fade-up"
        data-aos-delay="200"
      >
        <div className="admin-section-header">
          <div>
            <span>ALL PHOTOS</span>

            <h2>Gallery Photos</h2>
          </div>

          <button className="admin-outline-btn" onClick={openAddForm}>
            <FaPlus />
            Add Photo
          </button>
        </div>

        {gallery.length > 0 ? (
          <>
            <div className="admin-gallery-grid">
              {currentGallery.map((item, index) => (
                <div
                  className="admin-gallery-card"
                  key={item.id}
                  data-aos="fade-up"
                  data-aos-delay={index * 80}
                  data-aos-duration="700"
                >
                  <div className="admin-gallery-image">
                    <img src={item.image} alt={`Gallery ${item.id}`} />
                  </div>

                  <div className="admin-gallery-card-content">
                    <div>
                      <span className="gallery-category">
                        Photo #{item.display_order}
                      </span>

                      <h3>Gallery Image</h3>
                    </div>

                    <div className="admin-gallery-actions">
                      <button
                        type="button"
                        className="danger"
                        onClick={() => deleteGallery(item.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="gallery-pagination">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                <div className="pagination-pages">
                  {Array.from(
                    { length: totalPages },
                    (_, index) => index + 1,
                  ).map((page) => (
                    <button
                      type="button"
                      key={page}
                      className={`pagination-page ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="admin-gallery-empty">
            <FaImages />

            <h3>No Gallery Photos</h3>

            <p>Add your first gallery image.</p>

            <button className="admin-primary-btn" onClick={openAddForm}>
              <FaPlus />
              Add Photo
            </button>
          </div>
        )}
      </div>

      {/* MODAL */}

      {showForm && (
        <div className="admin-modal-overlay">
          <div className="admin-modal gallery-modal">
            {/* HEADER */}

            <div className="admin-modal-header">
              <div>
                <span>GALLERY PHOTO</span>

                <h2>Add Photo</h2>
              </div>

              <button
                type="button"
                onClick={closeForm}
                className="modal-close-btn"
              >
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <div className="admin-form">
              <label>
                Gallery Image
                <div
                  className="gallery-upload-box"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {preview ? (
                    <div className="gallery-upload-preview">
                      <img src={preview} alt="Preview" />

                      <div className="gallery-preview-overlay">
                        <FaUpload />

                        <span>Change Image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="gallery-upload-placeholder">
                      <FaImage />

                      <strong>Upload Gallery Image</strong>

                      <span>Click here to select an image</span>

                      <small>JPG, PNG, WEBP, GIF</small>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageChange}
                  className="gallery-file-input"
                />
              </label>

              {/* ACTIONS */}

              <div className="gallery-form-actions">
                <button
                  type="button"
                  className="admin-cancel-btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  type="button"
                  className="admin-save-btn"
                  onClick={saveGallery}
                  disabled={saving}
                >
                  <FaSave />

                  {saving ? "Uploading..." : "Save Photo"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
